import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormLayout } from '@/components/layout';
import { Checkbox } from '@/components/ui/inputs/Checkbox';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { Selector } from '@/components/ui/inputs/selector/Selector';
import type { SelectorOption } from '@/components/ui/inputs/selector/Selector.types';
import type { AttendanceConfigForm } from '@/schemas/attendance/attendanceConfiguration.schema';


interface AttendanceConfigurationValidationsFormProps {
  form: AttendanceConfigForm;
  roleOptions: SelectorOption[];
  automaticModeOptions: SelectorOption[];
}

const handleSubmit = (event: React.FormEvent) => {
  event.preventDefault();
};

export const AttendanceConfigurationValidationsForm: React.FC<
  AttendanceConfigurationValidationsFormProps
> = ({
  form,
  roleOptions,
  automaticModeOptions,
}) => {
  const { t: translate } = useTranslation('attendance');
  const fields = translate('configuration.fields', { returnObjects: true }) as Record<string, string>;
  const sections = translate('configuration.sections', { returnObjects: true }) as Record<string, string>;
  const tooltips = translate('configuration.tooltips', { returnObjects: true }) as Record<string, string>;
  const { watch, setValue, errors } = form;

  const translateError = (message: string | undefined) => message ? translate(message) : undefined;

  return (
    <FormLayout onSubmit={handleSubmit} columns={4}>
      {/* Sección Gestor de Marcas */}
      <FormLayout.Section title={sections.markManager}>
        <Checkbox
          label={fields.autoFillHolidays}
          tooltipText={tooltips.autoFillHolidays}
          checked={watch('autoFillHolidays')}
          onChange={(checked) => setValue('autoFillHolidays', checked, { shouldDirty: true })}
        />
        <Checkbox
          label={fields.autoFillVacations}
          tooltipText={tooltips.autoFillVacations}
          checked={watch('autoFillVacations')}
          onChange={(checked) => setValue('autoFillVacations', checked, { shouldDirty: true })}
        />
        <Checkbox
          label={fields.autoFillSickLeave}
          tooltipText={tooltips.autoFillSickLeave}
          checked={watch('autoFillSickLeave')}
          onChange={(checked) => setValue('autoFillSickLeave', checked, { shouldDirty: true })}
        />
        <Checkbox
          label={fields.autoFillSpecialPermissions}
          tooltipText={tooltips.autoFillSpecialPermissions}
          checked={watch('autoFillSpecialPermissions')}
          onChange={(checked) => setValue('autoFillSpecialPermissions', checked, { shouldDirty: true })}
        />
        <Checkbox
          label={fields.autoFillAdminPermissions}
          tooltipText={tooltips.autoFillAdminPermissions}
          checked={watch('autoFillAdminPermissions')}
          onChange={(checked) => setValue('autoFillAdminPermissions', checked, { shouldDirty: true })}
        />
        <Checkbox
          label={fields.autoFillFractionPermissions}
          tooltipText={tooltips.autoFillFractionPermissions}
          checked={watch('autoFillFractionPermissions')}
          onChange={(checked) => setValue('autoFillFractionPermissions', checked, { shouldDirty: true })}
        />
      </FormLayout.Section>

      {/* Sección Horas Extras */}
      <FormLayout.Section title={sections.overtimeHours}>
        <Checkbox
          label={fields.approvalFlow}
          tooltipText={tooltips.approvalFlow}
          checked={watch('approvalFlow')}
          onChange={(checked) => setValue('approvalFlow', checked, { shouldDirty: true })}
        />
        <Selector
          label={fields.reviewerProfile}
          options={roleOptions}
          value={watch('reviewerProfiles').map(String)}
          onChange={(value) => setValue('reviewerProfiles', (Array.isArray(value) ? value : [value]).filter(Boolean).map(Number), { shouldDirty: true })}
          multiple
          disabled={!watch('approvalFlow')}
        />
        <Selector
          label={fields.approverProfile}
          options={roleOptions}
          value={watch('approverProfiles').map(String)}
          onChange={(value) => setValue('approverProfiles', (Array.isArray(value) ? value : [value]).filter(Boolean).map(Number), { shouldDirty: true })}
          multiple
          disabled={!watch('approvalFlow')}
        />
      </FormLayout.Section>

      {/* Sección Pacto Compensación Feriados */}
      <FormLayout.Section title={sections.holidayCompensation}>
        <Selector
          label={fields.automaticMode}
          options={automaticModeOptions}
          value={watch('automaticMode')}
          onChange={(value) => setValue('automaticMode', value as string, { shouldDirty: true })}
        />
        <LabelInput
          label={fields.cutDaysSlack}
          type="number"
          value={String(watch('cutDaysSlack') ?? '')}
          onChange={(value) => setValue('cutDaysSlack', value === '' ? null : Number(value), { shouldDirty: true })}
          color={errors.cutDaysSlack ? 'error' : 'default'}
          supportingText={translateError(errors.cutDaysSlack?.message)}
        />
      </FormLayout.Section>
    </FormLayout>
  );
};
